import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'
import Rails from '@rails/ujs'

export default class extends Controller {
  static targets = [
    'dropdown',
    'triggerLabel',
    'startDateInput',
    'endDateInput',
    'compareStartDateInput',
    'compareEndDateInput',
    'calendar',
    'compareSection',
    'compareTypeSection',
    'presetItem',
    'compareTypeItem',
    'filterItem',
    'sourceItem',
    'form'
  ]

  static values = {
    preset: { type: String, default: 'last_28_days' },
    startDate: String,
    endDate: String,
    compareEnabled: { type: Boolean, default: false },
    compareType: { type: String, default: 'previous_period' },
    compareStartDate: String,
    compareEndDate: String,
    dateField: { type: String, default: '' },
    source: { type: String, default: '' }
  }

  connect() {
    this.initializeFlatpickr()
    this.updatePresetHighlight()
    this.updateCompareTypeHighlight()
    this.updateCompareVisibility()
    this.updateFilterHighlight()
    this.updateSourceHighlight()

    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    this.destroyFlatpickr()
  }

  initializeFlatpickr() {
    const commonOptions = {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'M j, Y',
      allowInput: false,
      disableMobile: true
    }

    if (this.hasStartDateInputTarget) {
      this.startPicker = flatpickr(this.startDateInputTarget, {
        ...commonOptions,
        defaultDate: this.startDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.startDateValue = this.formatDate(selectedDates[0])
            this.presetValue = 'custom'
            this.updatePresetHighlight()
            this.updateCalendar()
            this.updateComparisonDates()
          }
        }
      })
    }

    if (this.hasEndDateInputTarget) {
      this.endPicker = flatpickr(this.endDateInputTarget, {
        ...commonOptions,
        defaultDate: this.endDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.endDateValue = this.formatDate(selectedDates[0])
            this.presetValue = 'custom'
            this.updatePresetHighlight()
            this.updateCalendar()
            this.updateComparisonDates()
          }
        }
      })
    }

    if (this.hasCompareStartDateInputTarget) {
      this.compareStartPicker = flatpickr(this.compareStartDateInputTarget, {
        ...commonOptions,
        defaultDate: this.compareStartDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.compareStartDateValue = this.formatDate(selectedDates[0])
            this.compareTypeValue = 'custom'
            this.updateCompareTypeHighlight()
          }
        }
      })
    }

    if (this.hasCompareEndDateInputTarget) {
      this.compareEndPicker = flatpickr(this.compareEndDateInputTarget, {
        ...commonOptions,
        defaultDate: this.compareEndDateValue,
        onChange: (selectedDates) => {
          if (selectedDates[0]) {
            this.compareEndDateValue = this.formatDate(selectedDates[0])
            this.compareTypeValue = 'custom'
            this.updateCompareTypeHighlight()
          }
        }
      })
    }

    if (this.hasCalendarTarget) {
      this.inlineCalendar = flatpickr(this.calendarTarget, {
        mode: 'range',
        inline: true,
        dateFormat: 'Y-m-d',
        defaultDate: [this.startDateValue, this.endDateValue],
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.startDateValue = this.formatDate(selectedDates[0])
            this.endDateValue = this.formatDate(selectedDates[1])
            this.presetValue = 'custom'
            this.updatePresetHighlight()
            this.updateDateInputs()
            this.updateComparisonDates()
          }
        }
      })
    }
  }

  destroyFlatpickr() {
    if (this.startPicker) this.startPicker.destroy()
    if (this.endPicker) this.endPicker.destroy()
    if (this.compareStartPicker) this.compareStartPicker.destroy()
    if (this.compareEndPicker) this.compareEndPicker.destroy()
    if (this.inlineCalendar) this.inlineCalendar.destroy()
  }

  toggle(event) {
    event.stopPropagation()
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle('show')
    }
  }

  handleClickOutside(event) {
    if (
      !this.element.contains(event.target) &&
      !event.target.closest('.flatpickr-calendar') &&
      this.hasDropdownTarget
    ) {
      this.dropdownTarget.classList.remove('show')
    }
  }

  selectPreset(event) {
    event.preventDefault()
    const preset = event.currentTarget.dataset.preset
    this.presetValue = preset

    const dates = this.calculatePresetDates(preset)
    this.startDateValue = dates.start
    this.endDateValue = dates.end

    this.updateDateInputs()
    this.updateCalendar()
    this.updatePresetHighlight()

    if (preset === 'this_year' || preset === 'previous_year') {
      this.compareTypeValue = 'previous_year'
      this.updateCompareTypeHighlight()
    }
    this.updateComparisonDates()
    this.updateTriggerLabel()
  }

  calculatePresetDates(preset) {
    const today = new Date()
    let start, end

    switch (preset) {
      case 'today':
        start = today
        end = today
        break
      case 'yesterday':
        start = this.subtractDays(today, 1)
        end = this.subtractDays(today, 1)
        break
      case 'last_7_days':
        start = this.subtractDays(today, 6)
        end = today
        break
      case 'last_28_days':
        start = this.subtractDays(today, 27)
        end = today
        break
      case 'last_30_days':
        start = this.subtractDays(today, 29)
        end = today
        break
      case 'last_90_days':
        start = this.subtractDays(today, 89)
        end = today
        break
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1)
        end = today
        break
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        end = new Date(today.getFullYear(), today.getMonth(), 0)
        break
      case 'last_quarter': {
        const currentQuarterStart = Math.floor(today.getMonth() / 3) * 3
        end = new Date(today.getFullYear(), currentQuarterStart, 0)
        start = new Date(end.getFullYear(), end.getMonth() - 2, 1)
        break
      }
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1)
        end = today
        break
      case 'previous_year':
        start = new Date(today.getFullYear() - 1, 0, 1)
        end = new Date(today.getFullYear() - 1, 11, 31)
        break
      case 'last_12_months':
        start = this.subtractYears(today, 1)
        end = today
        break
      case 'custom':
      default:
        return { start: this.startDateValue, end: this.endDateValue }
    }

    return {
      start: this.formatDate(start),
      end: this.formatDate(end)
    }
  }

  toggleCompare(event) {
    this.compareEnabledValue = event.target.checked
    this.updateCompareVisibility()
    if (this.compareEnabledValue) {
      this.updateComparisonDates()
    }
  }

  selectCompareType(event) {
    event.preventDefault()
    const compareType = event.currentTarget.dataset.compareType
    this.compareTypeValue = compareType
    this.updateCompareTypeHighlight()
    this.updateComparisonDates()
  }

  updateCompareVisibility() {
    if (this.hasCompareSectionTarget) {
      this.compareSectionTarget.classList.toggle(
        'd-none',
        !this.compareEnabledValue
      )
    }
    if (this.hasCompareTypeSectionTarget) {
      this.compareTypeSectionTarget.classList.toggle(
        'd-none',
        !this.compareEnabledValue
      )
    }
  }

  updateComparisonDates() {
    if (!this.compareEnabledValue || this.compareTypeValue === 'custom') return

    const startDate = this.parseDate(this.startDateValue)
    const endDate = this.parseDate(this.endDateValue)
    const dates = this.calculateComparisonDates(
      this.compareTypeValue,
      startDate,
      endDate
    )

    this.compareStartDateValue = this.formatDate(dates.start)
    this.compareEndDateValue = this.formatDate(dates.end)

    this.updateCompareDateInputs()
  }

  calculateComparisonDates(type, startDate, endDate) {
    const daysDiff = Math.round(
      (endDate - startDate) / (1000 * 60 * 60 * 24)
    )

    switch (type) {
      case 'previous_period_match_dow': {
        const weeksToShift = Math.ceil((daysDiff + 1) / 7)
        return {
          start: this.subtractDays(startDate, weeksToShift * 7),
          end: this.subtractDays(endDate, weeksToShift * 7)
        }
      }
      case 'previous_period': {
        // Mirrors DateRangePresets.calculate_comparison_dates: a month to date
        // has no meaningful "period before" other than the month before it, and
        // the form posts these dates back for the server to use verbatim, so the
        // two have to agree or the picked comparison is not the one applied.
        if (this.isMonthToDate(startDate, endDate)) {
          const compareEnd = this.alignedPreviousMonthDay(endDate)
          return {
            start: new Date(
              compareEnd.getFullYear(),
              compareEnd.getMonth(),
              1
            ),
            end: compareEnd
          }
        }
        return {
          start: this.subtractDays(startDate, daysDiff + 1),
          end: this.subtractDays(startDate, 1)
        }
      }
      case 'previous_year':
        return {
          start: this.subtractYears(startDate, 1),
          end: this.subtractYears(endDate, 1)
        }
      case 'custom':
      default:
        return {
          start: this.parseDate(this.compareStartDateValue),
          end: this.parseDate(this.compareEndDateValue)
        }
    }
  }

  updateDateInputs() {
    if (this.startPicker) {
      this.startPicker.setDate(this.startDateValue, false)
    }
    if (this.endPicker) {
      this.endPicker.setDate(this.endDateValue, false)
    }
  }

  updateCompareDateInputs() {
    if (this.compareStartPicker) {
      this.compareStartPicker.setDate(this.compareStartDateValue, false)
    }
    if (this.compareEndPicker) {
      this.compareEndPicker.setDate(this.compareEndDateValue, false)
    }
  }

  updateCalendar() {
    if (this.inlineCalendar) {
      this.inlineCalendar.setDate(
        [this.startDateValue, this.endDateValue],
        false
      )
    }
  }

  // Every group in the dropdown marks its picked item the same way. Only
  // `active` — the panel's stylesheet owns the colour, where `bg-primary` would
  // hand it to the legacy dashboard.css and paint a different red.
  highlightItems(items, datasetKey, value) {
    items.forEach((item) => {
      item.classList.toggle('active', item.dataset[datasetKey] === value)
    })
  }

  updatePresetHighlight() {
    this.highlightItems(this.presetItemTargets, 'preset', this.presetValue)
  }

  updateCompareTypeHighlight() {
    this.highlightItems(this.compareTypeItemTargets, 'compareType', this.compareTypeValue)
  }

  selectFilter(event) {
    event.preventDefault()
    this.dateFieldValue = event.currentTarget.dataset.filterValue
    this.updateFilterHighlight()
  }

  updateFilterHighlight() {
    this.highlightItems(this.filterItemTargets, 'filterValue', this.dateFieldValue)
  }

  selectSource(event) {
    event.preventDefault()
    this.sourceValue = event.currentTarget.dataset.sourceValue
    this.updateSourceHighlight()
  }

  updateSourceHighlight() {
    this.highlightItems(this.sourceItemTargets, 'sourceValue', this.sourceValue)
  }

  updateTriggerLabel() {
    if (this.hasTriggerLabelTarget) {
      const startFormatted = this.formatDisplayDate(this.startDateValue)
      const endFormatted = this.formatDisplayDate(this.endDateValue)
      this.triggerLabelTarget.innerHTML = `${startFormatted} - ${endFormatted}`
    }
  }

  apply(event) {
    event.preventDefault()
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('show')
    }
    this.updateTriggerLabel()

    if (this.hasFormTarget) {
      this.formTarget.querySelector('input[name="preset"]').value =
        this.presetValue
      this.formTarget.querySelector('input[name="start_date"]').value =
        this.startDateValue
      this.formTarget.querySelector('input[name="end_date"]').value =
        this.endDateValue
      this.formTarget.querySelector('input[name="compare"]').value = this
        .compareEnabledValue
        ? 'true'
        : 'false'
      this.formTarget.querySelector('input[name="compare_type"]').value =
        this.compareTypeValue

      if (this.compareEnabledValue) {
        this.formTarget.querySelector(
          'input[name="compare_start_date"]'
        ).value = this.compareStartDateValue
        this.formTarget.querySelector(
          'input[name="compare_end_date"]'
        ).value = this.compareEndDateValue
      } else {
        this.formTarget.querySelector(
          'input[name="compare_start_date"]'
        ).value = ''
        this.formTarget.querySelector(
          'input[name="compare_end_date"]'
        ).value = ''
      }

      const dateFieldInput = this.formTarget.querySelector('input[name="date_field"]')
      if (dateFieldInput) dateFieldInput.value = this.dateFieldValue

      const sourceInput = this.formTarget.querySelector('input[name="source"]')
      if (sourceInput) sourceInput.value = this.sourceValue

      Rails.fire(this.formTarget, 'submit')
    }
  }

  cancel(event) {
    event.preventDefault()
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('show')
    }
  }

  // Utility methods
  formatDate(date) {
    if (typeof date === 'string') return date
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  formatDisplayDate(dateStr) {
    const date = this.parseDate(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  parseDate(dateStr) {
    if (!dateStr) return new Date()
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  subtractDays(date, days) {
    const result = new Date(date)
    result.setDate(result.getDate() - days)
    return result
  }

  subtractYears(date, years) {
    const result = new Date(date)
    result.setFullYear(result.getFullYear() - years)
    return result
  }

  // A range that runs from the first of a month to a day inside that same month.
  isMonthToDate(startDate, endDate) {
    return (
      startDate.getDate() === 1 &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear()
    )
  }

  // The same day of the previous month, or its last day when the previous month
  // is the shorter one (March 31 compares against February 28).
  alignedPreviousMonthDay(date) {
    const lastOfPreviousMonth = new Date(
      date.getFullYear(),
      date.getMonth(),
      0
    )
    return new Date(
      lastOfPreviousMonth.getFullYear(),
      lastOfPreviousMonth.getMonth(),
      Math.min(date.getDate(), lastOfPreviousMonth.getDate())
    )
  }
}
